import { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosRequestConfig } from 'axios';
import { debounce } from 'ts-debounce';

import { AxiosResponse } from 'helpers';

import { useMountedRef } from './useMounted';
import { useSnackContext } from './useSnackContext';

type DataRow = Record<string, any> & { id: number };

export type ControlledPaginationFunc<ResponseDatum extends DataRow> = (
  config: AxiosRequestConfig,
) => Promise<AxiosResponse<Array<ResponseDatum>>>;

/**
 * A hook that handles loading paginated data
 *
 * @param dataFetchFunc - Stable function for loading data
 * @param fetchConfig - Stable (memoized) config that is passed to the dataFetchFunc
 * @returns dataArray, dataLoaded, count, fetchData and updateData
 */
export const useControlledPagination = <ResponseDatum extends DataRow>(
  dataFetchFunc: ControlledPaginationFunc<ResponseDatum>,
  fetchConfig: AxiosRequestConfig = {},
) => {
  const [dataArray, setDataArray] = useState<Array<ResponseDatum>>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [count, setCount] = useState(0);
  const { setSnack } = useSnackContext();
  const mountedRef = useMountedRef();

  // Define a base debounced loading function
  const debounceFetchFunc = useMemo(
    () =>
      debounce(
        async (config: AxiosRequestConfig = {}) => {
          try {
            setDataLoaded(false);
            const { data: dataArr, headers } = await dataFetchFunc(config);

            mountedRef.current && setDataArray(dataArr);
            setCount(parseInt(headers['x-count'], 10) ?? 0);
            setDataLoaded(true);
          } catch (e) {
            setSnack((props) => ({
              ...props,
              severity: 'error',
              open: true,
              message: 'Failed To Fetch Data',
              error: e as Error,
            }));
            setDataArray([]);
            setDataLoaded(true);
          }
        },
        500, // 0.5 second
      ),
    [dataFetchFunc, setSnack, mountedRef],
  );

  // Define a function that provides the fetchConfig already in the function and allows for overriding
  const fetchData = useCallback(
    (overloadConfig?: AxiosRequestConfig) =>
      debounceFetchFunc({ ...fetchConfig, ...overloadConfig }),
    [debounceFetchFunc, fetchConfig],
  );

  // Load data any time fetchData is redefined (when fetchConfig changes)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateData = useCallback(
    (updateData: ResponseDatum) => {
      setDataArray((prev) =>
        prev.map((row) =>
          row.id === updateData.id ? { ...row, ...updateData } : row,
        ),
      );
    },
    [setDataArray],
  );

  return {
    dataArray,
    dataLoaded,
    count,
    fetchData,
    updateData,
  };
};
